import React, { createContext, useContext, useState, useEffect } from 'react';
import firebaseService from '../services/firebaseService';

const SettingsContext = createContext();

export function useSettings() {
  return useContext(SettingsContext);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    distanceUnit: 'auto', // 'auto', 'miles', 'kilometers'
    theme: 'light',
    notifications: true
  });
  const [featureFlags, setFeatureFlags] = useState({
    GoogleSignin: true // Default to true until fetched
  });

  useEffect(() => {
    // Load feature flags
    const fetchFlags = async () => {
      const flags = await firebaseService.getFeatureFlags();
      if (Object.keys(flags).length > 0) {
        setFeatureFlags(prev => ({ ...prev, ...flags }));
      }
    };
    fetchFlags();

    // Load settings from localStorage
    const storedSettings = localStorage.getItem('crapperMapper_settings');
    if (storedSettings) {
      try {
        const parsedSettings = JSON.parse(storedSettings);
        setSettings({ ...settings, ...parsedSettings });
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    } else {
      // Auto-detect regional preference
      const userLocale = navigator.language || 'en-US';
      const isUSRegion = userLocale.includes('US') || userLocale.includes('en-US');
      const autoUnit = isUSRegion ? 'miles' : 'kilometers';
      
      const defaultSettings = {
        ...settings,
        distanceUnit: autoUnit
      };
      
      setSettings(defaultSettings);
      localStorage.setItem('crapperMapper_settings', JSON.stringify(defaultSettings));
    }
  }, []);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('crapperMapper_settings', JSON.stringify(newSettings));
  };

  const getDistanceUnit = () => {
    if (settings.distanceUnit === 'auto') {
      const userLocale = navigator.language || 'en-US';
      return userLocale.includes('US') || userLocale.includes('en-US') ? 'miles' : 'kilometers';
    }
    return settings.distanceUnit;
  };

  const formatDistance = (distanceInKm) => {
    const unit = getDistanceUnit();
    if (unit === 'miles') {
      const miles = distanceInKm * 0.621371;
      return `${miles.toFixed(1)} mi`;
    } else {
      return `${distanceInKm.toFixed(1)} km`;
    }
  };

  const convertDistance = (distanceInKm) => {
    const unit = getDistanceUnit();
    if (unit === 'miles') {
      return distanceInKm * 0.621371;
    }
    return distanceInKm;
  };

  const value = {
    settings,
    featureFlags,
    updateSetting,
    getDistanceUnit,
    formatDistance,
    convertDistance
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}
